// User-Branch Management Interfaces
export interface IUser {
  id: string; // Changed from number to string to match API response
  uuid: string;
  fullname: string;
  username: string | null;
  line_user_id: string;
  line_display_name: string;
  line_display_image?: string | null;
  phone: string;
  verified_phone?: string | null;
  phone_verified_at?: string | null;
  email: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  legacy_branch_uuid?: string;
}

export interface IBranch {
  id: number | string;
  uuid: string;
  branch_name?: string; // legacy field
  branch_code?: string; // legacy field
  name?: string; // new API response field
  code?: string; // new API response field
  address?: string;
  phone?: string;
  district?: string;
  province?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  franchise_uuid?: string | null;
  branch_zone_id?: string;
  delivery_schedule_id?: string;
  assigned_at?: string; // When branch was assigned to user
  // User-Branch relationship fields (from User Branches API)
  role?: 'PRIMARY' | 'SECONDARY';
  can_manage_users?: boolean;
  used_branch_phone_to_verify?: boolean;
  invited_by_user_uuid?: string | null;
  invited_at?: string | null;
  is_default?: boolean;
  created_at?: string;
  // Relations (available in single branch API)
  franchise?: {
    id: number;
    uuid: string;
    name: string;
    franchise_code: string;
  };
  zone?: any;
  deliverySchedule?: any;
}

// API Response Interfaces
export interface IPagination {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface IPaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface IUserWithBranchesAPIResponse {
  success: boolean;
  data: IUserWithBranches[];
  pagination: IPagination;
}

export interface IBranchAPIResponse {
  data: IBranch[];
  meta: IPaginationMeta;
}

export interface IUserWithBranches {
  user: IUser;
  branches: IBranch[];
  branch_count: number;
  has_phone_verification_issue?: boolean;
}

export interface IUserBranchRelationship {
  id: number;
  uuid: string;
  user_uuid: string;
  branch_uuid: string;
  customer_branch: IBranch;
  user_master: IUser;
}

// =====================================================
// User Branches Response (GET /user-customer-branch/user/:userUuid/branches)
// =====================================================

export interface IDefaultBranchInfo {
  default_branch_uuid: string | null;
  has_default_branch: boolean;
  is_valid: boolean;
  warning?: string;
  issue?: string;
}

export interface IUserInfo {
  uuid: string;
  fullname: string;
  username: string;
  line_user_id?: string;
}

export interface IUserBranchesResponse {
  branches: IBranch[];
  default_branch_info: IDefaultBranchInfo;
  user_info: IUserInfo;
}

// Set Default Branch Request & Response
export interface ISetDefaultBranchRequest {
  user_uuid: string;
  branch_uuid: string;
}

export interface ISetDefaultBranchResponse {
  success: boolean;
  message: string;
  data: {
    user_uuid: string;
    user_name: string;
    default_branch_uuid: string;
    branch_name: string;
    branch_code: string;
  };
}

// Dashboard Stats Interfaces
export interface IPopularBranch {
  id: string;
  name: string;
  code: string;
  user_count: number;
}

export interface IDashboardStatsData {
  users: {
    total: number;
    active: number;
    inactive: number;
    with_branches: number;
    without_branches: number;
  };
  branches: {
    total: number;
    popular: IPopularBranch[];
  };
  assignments: {
    total: number;
    unique_users: number;
  };
}

export interface IDashboardStatsResponse {
  success: boolean;
  data: IDashboardStatsData;
}

// Legacy interface for backward compatibility
export interface IDashboardStats {
  totalUsers: number;
  totalBranches: number;
  totalRelationships: number;
  usersWithMultipleBranches: number;
  usersWithSingleBranch: number;
  usersWithLegacyBranch: number;
  migrationNeeded: boolean;
  averageBranchesPerUser: string;
}

export interface IAssignBranchRequest {
  user_uuid: string;
  branch_uuid: string;
}

export interface IRemoveBranchRequest {
  user_uuid: string;
  branch_uuid: string;
}

// Remove All Branches Request & Response Interfaces
export interface IRemoveAllBranchesRequest {
  user_uuid: string;
}

export interface IRemoveAllBranchesResponse {
  success: boolean;
  message: string;
  data: {
    user_uuid: string;
    removed_branches_count: number;
    removed_legacy_branch: boolean;
    operation_details: {
      step1_user_customer_branch: string;
      step2_user_master_cleanup: string;
    };
  } | null;
}

export interface IUserBranchAPIResponse {
  success: boolean;
  message: string;
  data?: IUserBranchRelationship | null;
}

// Form Values
export interface IUserBranchSearchForm {
  keyword: string;
  filterType: 'all' | 'single' | 'multiple';
  province: string;
  district: string;
}

export interface IBranchAssignmentForm {
  selectedBranches: string[];
}

// Default Values
export const DUserBranchSearchForm: IUserBranchSearchForm = {
  keyword: '',
  filterType: 'all',
  province: '',
  district: '',
};

export const DBranchAssignmentForm: IBranchAssignmentForm = {
  selectedBranches: [],
};

// Enums
export enum EUserBranchQueryKeys {
  GET_USERS_WITH_BRANCHES = 'GET_USERS_WITH_BRANCHES',
  GET_ALL_BRANCHES = 'GET_ALL_BRANCHES',
  GET_DASHBOARD_STATS = 'GET_DASHBOARD_STATS',
  GET_USER_BRANCHES = 'GET_USER_BRANCHES',
  GET_MIGRATION_STATUS = 'GET_MIGRATION_STATUS',
  SET_DEFAULT_BRANCH = 'SET_DEFAULT_BRANCH',
}

export type TFilterType = 'all' | 'single' | 'multiple';

// =====================================================
// Admin User Management Interfaces (Multi-User Branch)
// =====================================================

// Branch LINE User (from /admin/user-management/branch/:uuid/detail)
export interface ILineUser {
  user_uuid: string;
  user_customer_branch_uuid?: string;
  fullname: string;
  phone: string;
  verified_phone?: string | null;
  phone_verified_at?: string | null;
  used_branch_phone_to_verify?: boolean;
  line_user_id: string;
  line_display_name: string;
  line_display_image: string;
  role: 'PRIMARY' | 'SECONDARY';
  can_manage_users: boolean;
  invited_by_user_uuid?: string | null;
  invited_by_fullname?: string | null;
  invited_at?: string | null;
  created_at: string;
}

// Update User Phone Request (PATCH /admin/user-management/update-user-phone)
export interface IAdminUpdateUserPhoneRequest {
  user_uuid: string;
  phone?: string;
  verified_phone?: string;
  note?: string;
}

// Update User Phone Response
export interface IAdminUpdateUserPhoneResponse {
  success: boolean;
  message: string;
  updated_fields: string[];
}

// Branch Detail Response (from /admin/user-management/branch/:uuid/detail)
export interface IAdminBranchDetailResponse {
  uuid: string;
  branch_code: string;
  branch_name: string;
  phone: string;
  address: string;
  province: string;
  franchise_uuid: string;
  window_time_slots: any | null;
  delivery_address: string | null;
  users: ILineUser[];
  total_users: number;
  primary_user: ILineUser | null;
  active_invite_tokens: number;
}

// Add User to Branch Request (POST /admin/user-management/add-user-to-branch)
export interface IAdminAddUserToBranchRequest {
  user_uuid: string;
  branch_uuid: string;
  role: 'PRIMARY' | 'SECONDARY';
  note?: string;
}

// Add User to Branch Response
export interface IAdminAddUserToBranchResponse {
  success: boolean;
  message: string;
  user_customer_branch_uuid?: string;
  user?: ILineUser;
}

// Set Primary User Request (POST /admin/user-management/set-primary-user)
export interface IAdminSetPrimaryUserRequest {
  branch_uuid: string;
  user_uuid: string;
  force?: boolean;
}

// Set Primary User Response
export interface IAdminSetPrimaryUserResponse {
  success: boolean;
  message: string;
}

// Remove User from Branch Response (DELETE /admin/user-management/user/:uuid/branch/:uuid)
export interface IAdminRemoveUserFromBranchResponse {
  success: boolean;
  message: string;
}

// Demote to Secondary Request (PATCH /admin/user-management/demote-to-secondary)
export interface IAdminDemoteToSecondaryRequest {
  user_uuid: string;
  branch_uuid: string;
  note?: string;
}

// Demote to Secondary Response
export interface IAdminDemoteToSecondaryResponse {
  success: boolean;
  message: string;
  previous_role: string;
}

// Admin Users List Query Params (GET /admin/user-management/users)
export interface IAdminUsersQueryParams {
  page?: number;
  limit?: number;
  franchise_uuid?: string;
  branch_uuid?: string;
  role?: 'PRIMARY' | 'SECONDARY';
  search?: string;
}

export interface IAdminUserCustomerCompany {
  uuid: string;
  customer_uuid: string;
  customer_code: string;
  company_name: string;
  tax_id?: string | null;
  company_branch_no?: string | null;
  access_level: 'viewer' | 'manager' | string;
  assigned_at: string;
  is_active: boolean;
}

// Admin Users List Response
export interface IAdminUserItem {
  user_uuid: string;
  fullname: string;
  phone: string;
  line_user_id: string;
  line_display_name?: string;
  line_display_image?: string;
  franchise_uuid?: string;
  branches: Array<{
    branch_uuid: string;
    branch_name: string;
    branch_code: string;
    role: 'PRIMARY' | 'SECONDARY';
    can_manage_users: boolean;
    created_at: string;
  }>;
  total_branches: number;
  customer_companies?: IAdminUserCustomerCompany[];
  total_customer_companies?: number;
  primary_branches_count: number;
  created_at: string;
}

export interface IAdminUsersListResponse {
  users: IAdminUserItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics: {
    total_users: number;
    total_primary_users: number;
    total_secondary_users: number;
    users_without_branch: number;
  };
}

export interface IAdminSetUserCustomerCompaniesRequest {
  customer_uuids: string[];
  access_level?: 'viewer' | 'manager' | string;
  note?: string;
}

export interface IAdminUserCustomerCompaniesResponse {
  user_uuid: string;
  customer_companies: IAdminUserCustomerCompany[];
  total_customer_companies: number;
}

// Admin User Management Query Keys
export enum EAdminUserManagementQueryKeys {
  GET_BRANCH_DETAIL = 'ADMIN_GET_BRANCH_DETAIL',
  GET_USERS_LIST = 'ADMIN_GET_USERS_LIST',
  GET_USER_CUSTOMER_COMPANIES = 'ADMIN_GET_USER_CUSTOMER_COMPANIES',
}
